import { useState, useEffect, useRef, useMemo } from "react";
import { DismissibleTooltip } from "@/components/ui/dismissible-tooltip";
import { UseFormReturn } from "react-hook-form";
import {
  formatTramsInvoiceDisplayNumber,
  TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH,
} from "@shared/invoice-display";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown, Trash2, Star, AlertTriangle, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  useTramsSuppliers,
  type TramsSupplier,
} from "@/hooks/useSuppliers";

import {
  TRAVEL_TYPES,
  TRAVEL_TYPE_BY_LINKNO,
  canonicalizeTravelTypeLabel,
  isSelectableTravelType,
  isReadOnlyTravelType,
  travelTypeDisplayLabel,
} from "./travelType";

export { TRAVEL_TYPES, canonicalizeTravelTypeLabel, isSelectableTravelType };

// TRAVELTYPE_LINKNO → canonical label, derived from the shared single source of
// truth and filtered to the user-selectable subset so a supplier's non-selectable
// TRAMS travel types (e.g. Domestic Air, Service Fee, Misc) never surface as
// picker options. Keeps this map in lockstep with @shared/travel-type.
const SELECTABLE_LABEL_BY_LINKNO: Record<number, string> = Object.fromEntries(
  Object.entries(TRAVEL_TYPE_BY_LINKNO)
    .filter(([, label]) => (TRAVEL_TYPES as readonly string[]).includes(label))
    .map(([linkNo, label]) => [Number(linkNo), label]),
);

const CRUISE_TYPES = new Set(["Ship – Deposit", "Ship – Final"]);
const TOUR_TYPES = new Set(["Tour – Deposit", "Tour – Final"]);
const HOTEL_TYPE = "Hotel";

// For Cruise and Tour, collapse Deposit/Final into the Final variant only.
const TRAVEL_TYPE_COLLAPSE_TO_FINAL: Record<number, (typeof TRAVEL_TYPES)[number]> = {
  4: "Ship – Final",   // Ship – Deposit → Ship – Final
  9: "Tour – Final",   // Tour – Deposit → Tour – Final
};

/**
 * Pretty-format a monetary input onBlur: strips $ / commas / whitespace,
 * pads to exactly 2 decimal places, and adds thousands separators when >= $1,000.
 * Invalid input is returned unchanged so Zod can surface the validation error.
 */
export function formatMoneyFieldValue(raw: string): string {
  if (!raw) return raw;
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return raw;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return raw;
  const rounded = Math.round(n * 100) / 100;
  if (rounded >= 1000) {
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return rounded.toFixed(2);
}

function stripMoneyFormatting(raw: string): string {
  return raw.replace(/[$,\s]/g, "");
}

// Monetary amount — always normalize to "NNN.NN" (2 decimal places).
// Accepts bare integers ("300") and auto-pads to "300.00".
const MonetaryAmount = (label: string) =>
  z
    .string()
    .min(1, `${label} is required`)
    .transform((raw) => raw.replace(/[$,\s]/g, ""))
    .refine((v) => /^\d+(\.\d{1,2})?$/.test(v), {
      message: `${label} must be a number with up to 2 decimal places (e.g. 300 or 300.00)`,
    })
    .transform((v) => {
      const n = Number(v);
      return (Math.round(n * 100) / 100).toFixed(2);
    });

export const invoiceFormSchema = z
  .object({
    travelerFirstName: z
      .string()
      .min(1, "First name is required")
      .max(100, "Must be at most 100 characters"),
    travelerLastName: z
      .string()
      .min(1, "Last name is required")
      .max(100, "Must be at most 100 characters"),
    supplierLinkNo: z.number().int().nonnegative().optional(),
    supplierName: z.string().min(1, "Supplier is required"),
    travelType: z.string().min(1, "Travel type is required"),
    propertyName: z.string().optional(),
    shipName: z.string().optional(),
    cabinCount: z.coerce.number().int().positive().optional(),
    tourName: z.string().optional(),
    description: z.string().optional(),
    confirmationNumber: z
      .string()
      .min(1, "Confirmation number is required")
      .max(
        TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH,
        `Confirmation number must be ${TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH} characters or fewer`,
      ),
    departureDate: z.string().min(1, "Departure date is required"),
    returnDate: z.string().min(1, "Return date is required"),
    totalFare: MonetaryAmount("Total invoice amount"),
    commissionAmount: MonetaryAmount("Commission amount"),
    agencyLinkNo: z.number().int().nonnegative(),
  })
  .refine(
    (d) => {
      if (!d.departureDate || !d.returnDate) return true;
      return new Date(d.returnDate) >= new Date(d.departureDate);
    },
    { message: "Return date must be after departure", path: ["returnDate"] }
  )
  .refine(
    (d) => {
      if (CRUISE_TYPES.has(d.travelType) && (!d.shipName || !d.shipName.trim()))
        return false;
      return true;
    },
    {
      message: "Ship name is required for cruise travel types",
      path: ["shipName"],
    }
  )
  .refine(
    (d) => {
      if (CRUISE_TYPES.has(d.travelType) && (!d.cabinCount || d.cabinCount < 1))
        return false;
      return true;
    },
    {
      message: "Cabin count is required for cruise travel types",
      path: ["cabinCount"],
    }
  );

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

function isEmptyFormValue(value: string | number | undefined | null): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "number") return false;
  return String(value).trim() === "";
}

function formatDisplayDate(isoOrDate: string): string {
  if (!isoOrDate || isoOrDate.length < 10) return isoOrDate;
  const d = isoOrDate.slice(0, 10);
  const [y, m, day] = d.split("-");
  if (!y || !m || !day) return isoOrDate;
  return `${m}/${day}/${y}`;
}

interface InvoiceFormCardProps {
  form: UseFormReturn<InvoiceFormValues>;
  index: number;
  /** TRAMS INVOICE.INVOICENUMBER for display (metadata.tramsInvoiceNumber). */
  tramsInvoiceNumber?: string | null;
  onRemove?: () => void;
  canRemove: boolean;
  /**
   * Suppresses the rendering of the Total Invoice Amount + Commission Amount
   * inputs. The form still tracks both values via `defaultValues` and
   * submits them to the server — only the visible UI is hidden.
   *
   * Used by the unclaimed-commission claim wizard, which pre-fills both
   * amounts from the TRAMS snapshot and intentionally does not surface
   * them to the advisor (Mr. Pickles, 2026-05-08).
   */
  hideFinancialFields?: boolean;
  /**
   * Unclaimed claim wizard: hide TRAMS-pre-filled inputs and show only trip,
   * traveler names, travel-type extras, and empty snapshot fields.
   */
  compactMode?: boolean;
}

export default function InvoiceFormCard({
  form,
  index,
  tramsInvoiceNumber,
  onRemove,
  canRemove,
  hideFinancialFields = false,
  compactMode = false,
}: InvoiceFormCardProps) {
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierOpen, setSupplierOpen] = useState(false);

  // -----------------------------------------------------------------------
  // TRAMS SUPPLIER SOURCE (nightly-synced `trams_suppliers` table).
  // Invoices are strictly TRAMS-only: dropdown rows come from trams_suppliers
  // and allowed travel types come from trams_supplier_travel_types (join),
  // exposed on each supplier as `travelTypes`.
  // -----------------------------------------------------------------------
  const { data: tramsResponse, isLoading: tramsLoading } = useTramsSuppliers(true);
  const tramsSuppliers = tramsResponse?.suppliers ?? [];

  const displayTramsSuppliers = useMemo(() => {
    if (supplierSearch.length === 0) return tramsSuppliers.slice(0, 100);
    const q = supplierSearch.toLowerCase();
    return tramsSuppliers.filter((s) => s.name?.toLowerCase().includes(q));
  }, [tramsSuppliers, supplierSearch]);

  const travelType = form.watch("travelType");
  const departureDate = form.watch("departureDate");
  const selectedSupplierLinkNo = form.watch("supplierLinkNo");
  const selectedSupplierName = form.watch("supplierName");
  const confirmationNumber = form.watch("confirmationNumber");
  const returnDate = form.watch("returnDate");

  const selectedTramsSupplier: TramsSupplier | undefined = tramsSuppliers.find(
    (s) => s.profileNo === selectedSupplierLinkNo,
  );

  /** Allowed travel types for the selected supplier, driven exclusively by the
   *  trams_supplier_travel_types join. Empty until a supplier is selected.
   *  Cruise (Ship) and Tour Deposit/Final pairs are collapsed to Final only. */
  const allowedTravelTypes = useMemo<readonly string[]>(() => {
    if (!selectedTramsSupplier) return [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const tt of selectedTramsSupplier.travelTypes) {
      const collapsed = TRAVEL_TYPE_COLLAPSE_TO_FINAL[tt.travelTypeLinkNo];
      const label = collapsed ?? SELECTABLE_LABEL_BY_LINKNO[tt.travelTypeLinkNo];
      if (label && !seen.has(label)) {
        seen.add(label);
        result.push(label);
      }
    }
    return result;
  }, [selectedTramsSupplier]);

  // Spec: warn when the selected TRAMS supplier has zero travel types in the join table.
  const showNoTravelTypesWarning =
    !!selectedTramsSupplier && selectedTramsSupplier.travelTypes.length === 0;

  const supplierPrefilled =
    !isEmptyFormValue(selectedSupplierLinkNo) &&
    !isEmptyFormValue(selectedSupplierName);
  const travelTypePrefilled = !isEmptyFormValue(travelType);

  // Unclaimed compact: supplier/traveler come from the snapshot; travel type may
  // be blank or the supplier may not resolve in trams_suppliers — still require
  // a pick from the canonical list instead of a dead-end hint.
  const compactNeedsTravelTypePick =
    compactMode && supplierPrefilled && !travelTypePrefilled;

  const travelTypeOptions = useMemo<readonly string[]>(() => {
    if (allowedTravelTypes.length > 0) return allowedTravelTypes;
    if (compactNeedsTravelTypePick) return TRAVEL_TYPES;
    return [];
  }, [allowedTravelTypes, compactNeedsTravelTypePick]);

  // Canonical form of the stored travel type (dash/spacing/alias-insensitive) so
  // selectable variants like "Tour - Final" / "Ship Final" / "Cruise" are treated
  // the same as their canonical "Tour – Final" / "Ship – Final" labels.
  const canonicalTravelType = travelType ? canonicalizeTravelTypeLabel(travelType) : travelType;

  // A travel type stored on the invoice that TRAMS owns and the user may not pick
  // (Domestic Air, Intl Air, Service Fee, Misc, Ancillary Air, Non ARC Air,
  // Southwest) — shown read-only. Unknown persisted labels are deliberately NOT
  // read-only so a bad import can be replaced via the selector/reset flow.
  const isNonSelectableTravelType = isReadOnlyTravelType(canonicalTravelType);

  // 2+ → selector; 1 → auto-selected below; 0 → hidden (warning/hint elsewhere).
  const showTravelTypeSelector = travelTypeOptions.length >= 2;
  const showTravelTypePickSupplierHint =
    !travelType &&
    travelTypeOptions.length === 0 &&
    !supplierPrefilled &&
    !compactNeedsTravelTypePick;

  const prevSupplierRef = useRef<{ linkNo: number | undefined; name: string | undefined }>({
    linkNo: selectedSupplierLinkNo,
    name: selectedSupplierName,
  });
  const supplierUserChanged = useRef(false);

  useEffect(() => {
    const prev = prevSupplierRef.current;
    const changed =
      prev.linkNo !== selectedSupplierLinkNo ||
      prev.name !== selectedSupplierName;
    prevSupplierRef.current = { linkNo: selectedSupplierLinkNo, name: selectedSupplierName };
    if (!changed) return;
    supplierUserChanged.current = true;
  }, [selectedSupplierLinkNo, selectedSupplierName]);

  useEffect(() => {
    // Never overwrite a TRAMS-driven non-selectable value; it renders read-only.
    if (isNonSelectableTravelType) return;
    if (travelTypeOptions.length === 1) {
      form.setValue("travelType", travelTypeOptions[0]);
    } else if (
      supplierUserChanged.current &&
      travelType &&
      !travelTypeOptions.includes(travelType)
    ) {
      form.setValue("travelType", "");
    }
    // `supplierUserChanged` is a ref — we intentionally read `.current` inline
    // rather than list it in deps (React doesn't track ref mutations).
  }, [travelTypeOptions, travelType, isNonSelectableTravelType, form]);

  // Normalize legacy dash/spacing/alias variants to the canonical label so the
  // selector pre-selects correctly and the read-only display is consistent.
  useEffect(() => {
    if (travelType && canonicalTravelType !== travelType) {
      form.setValue("travelType", canonicalTravelType);
    }
  }, [travelType, canonicalTravelType, form]);

  // Compare against the canonical label so legacy dash/spacing/alias variants
  // (e.g. "Tour - Final", "Ship Final") drive the correct fields on the first
  // render, without waiting for the normalization effect to rewrite the value.
  const isTourType = TOUR_TYPES.has(canonicalTravelType);
  const showPropertyName =
    canonicalTravelType === HOTEL_TYPE ||
    (isTourType && !!selectedTramsSupplier?.tourPropertyVisible);
  const showShipFields = CRUISE_TYPES.has(canonicalTravelType);
  const showTourName = isTourType && !!selectedTramsSupplier?.tourNameVisible;
  const showDescription =
    !!canonicalTravelType &&
    canonicalTravelType !== HOTEL_TYPE &&
    !CRUISE_TYPES.has(canonicalTravelType) &&
    !TOUR_TYPES.has(canonicalTravelType);
  const showExpediaHint = !!selectedTramsSupplier?.expediaBookable;
  const confirmationPrefilled = !isEmptyFormValue(confirmationNumber);
  const datesPrefilled =
    !isEmptyFormValue(departureDate) && !isEmptyFormValue(returnDate);

  const showSupplierField =
    !compactMode ||
    !supplierPrefilled ||
    showNoTravelTypesWarning ||
    showExpediaHint;
  const showTravelTypeField = !compactMode || !travelTypePrefilled;
  const showConfirmationField = !compactMode || !confirmationPrefilled;
  const showTravelDatesField = !compactMode || !datesPrefilled;

  // Travel-type extras are always shown when applicable — advisors may need to fill them.
  const showPropertyNameField = showPropertyName;
  const showShipNameField = showShipFields;
  const showCabinCountField = showShipFields;
  const showTourNameField = showTourName;
  const showDescriptionField = showDescription;

  const compactSummaryItems: { label: string; value: string }[] = [];
  if (compactMode) {
    if (supplierPrefilled && selectedSupplierName) {
      compactSummaryItems.push({
        label: "Supplier",
        value: selectedSupplierName,
      });
    }
    if (travelTypePrefilled) {
      compactSummaryItems.push({
        label: "Travel type",
        value: travelType ? travelTypeDisplayLabel(travelType) : travelType,
      });
    }
    if (confirmationPrefilled) {
      compactSummaryItems.push({
        label: "Confirmation",
        value: confirmationNumber,
      });
    }
    if (datesPrefilled) {
      compactSummaryItems.push({
        label: "Travel dates",
        value: `${formatDisplayDate(departureDate)} – ${formatDisplayDate(returnDate)}`,
      });
    }
  }

  const cardContent = (
    <>
        {compactMode && compactSummaryItems.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm">
            {compactSummaryItems.map(({ label, value }) => (
              <div key={label} className="min-w-0">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="truncate font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {/* Traveler Name (First + Last) — AR-316: stacks below sm, the labels
            wrap and misalign the inputs in two columns at 375px. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="travelerFirstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Traveler First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Martha" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="travelerLastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Traveler Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Jones" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Supplier Selection */}
        {showSupplierField && (
        <FormField
          control={form.control}
          name="supplierLinkNo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Supplier *</FormLabel>
              <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between font-normal",
                        !field.value &&
                          !selectedSupplierName &&
                          "text-muted-foreground",
                      )}
                    >
                      <span className="flex items-center gap-2 truncate">
                        {selectedTramsSupplier?.isPreferred && (
                          <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />
                        )}
                        <span className="truncate">
                          {selectedTramsSupplier?.name ||
                            selectedSupplierName ||
                            "Select supplier..."}
                        </span>
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                {/* AR-316: 400px overflows a 375px viewport */}
                <PopoverContent
                  className="w-[min(400px,calc(100vw-2rem))] p-0"
                  align="start"
                  collisionPadding={8}
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search suppliers..."
                      value={supplierSearch}
                      onValueChange={setSupplierSearch}
                    />
                    <CommandList>
                      {tramsLoading && (
                        <div className="p-4 text-sm text-muted-foreground text-center">
                          Loading suppliers...
                        </div>
                      )}
                      <CommandEmpty>No suppliers found.</CommandEmpty>
                      <CommandGroup>
                        <ScrollArea className="h-[200px]">
                          {displayTramsSuppliers.map((supplier) => (
                            <CommandItem
                              key={supplier.profileNo}
                              value={String(supplier.profileNo)}
                              onSelect={() => {
                                field.onChange(supplier.profileNo);
                                form.setValue("supplierName", supplier.name);
                                setSupplierOpen(false);
                                setSupplierSearch("");
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1">
                                  <div className="font-medium flex items-center gap-1.5">
                                    {supplier.isPreferred && (
                                      <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                                    )}
                                    <span>{supplier.name}</span>
                                  </div>
                                  {supplier.travelTypes.length > 0 && (
                                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-1">
                                      {supplier.travelTypes.slice(0, 3).map((tt) => (
                                        <Badge
                                          key={tt.travelTypeLinkNo}
                                          variant="outline"
                                          className="text-xs px-1 py-0 font-normal"
                                        >
                                          {tt.travelTypeName}
                                        </Badge>
                                      ))}
                                      {supplier.travelTypes.length > 3 && (
                                        <span className="text-xs">
                                          +{supplier.travelTypes.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {selectedSupplierLinkNo === supplier.profileNo && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </ScrollArea>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
              {showNoTravelTypesWarning && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This supplier has no travel types configured. Contact an
                    administrator before creating an invoice for this vendor.
                  </span>
                </div>
              )}
              {showExpediaHint && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 p-2 text-xs text-blue-800 dark:border-blue-800/60 dark:bg-blue-900/20 dark:text-blue-300">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>Booked through Expedia?</strong> If this booking was
                    made through Expedia, please select Expedia as the supplier.
                  </span>
                </div>
              )}
            </FormItem>
          )}
        />
        )}

        {/* Travel Type — driven strictly by trams_supplier_travel_types.
            - 0 types: hidden (amber warning under Supplier explains it)
            - 1 type: auto-selected, shown read-only
            - 2+ types: full selector */}
        {showTravelTypeField && (showTravelTypeSelector && !isNonSelectableTravelType ? (
          <FormField
            control={form.control}
            name="travelType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Travel Type *</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select travel type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {travelTypeOptions.map((tt) => (
                      <SelectItem key={tt} value={tt}>
                        {travelTypeDisplayLabel(tt)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : travelType ? (
          <div className="text-sm text-muted-foreground">
            Travel Type:{" "}
            <span className="font-medium text-foreground">
              {travelTypeDisplayLabel(travelType)}
            </span>
          </div>
        ) : showTravelTypePickSupplierHint ? (
          <div className="text-sm text-muted-foreground">
            Select a supplier to choose a travel type.
          </div>
        ) : null)}

        {/* Conditional Detail Fields */}
        {showPropertyNameField && (
          <FormField
            control={form.control}
            name="propertyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      TOUR_TYPES.has(travelType)
                        ? "Resort / hotel at tour destination"
                        : "Hotel property name"
                    }
                    {...field}
                    value={field.value != null ? field.value : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {(showShipNameField || showCabinCountField) && (
          <>
            {showShipNameField && (
            <FormField
              control={form.control}
              name="shipName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ship Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Cruise ship name"
                      {...field}
                      value={field.value != null ? field.value : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            )}
            {showCabinCountField && (
            <FormField
              control={form.control}
              name="cabinCount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cabin Count *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      placeholder="Number of cabins"
                      {...field}
                      value={field.value != null ? field.value : ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value, 10) : undefined,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            )}
          </>
        )}

        {showTourNameField && (
          <FormField
            control={form.control}
            name="tourName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tour Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Tour name"
                    {...field}
                    value={field.value != null ? field.value : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {showDescriptionField && (
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Description"
                    {...field}
                    value={field.value != null ? field.value : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Confirmation Number */}
        {showConfirmationField && (
        <FormField
          control={form.control}
          name="confirmationNumber"
          render={({ field, fieldState }) => {
            const length = (field.value ?? "").length;
            const overLimit = length > TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH;
            return (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormLabel>Confirmation Number *</FormLabel>
                  <TooltipProvider delayDuration={200}>
                    <DismissibleTooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Confirmation number help"
                          className="inline-flex text-muted-foreground hover:text-foreground"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Enter only the confirmation number itself (no labels or
                        notes) — {TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH} characters max.
                      </TooltipContent>
                    </DismissibleTooltip>
                  </TooltipProvider>
                </div>
                <FormControl>
                  <Input
                    placeholder="XYZG123456"
                    aria-invalid={overLimit || undefined}
                    className={overLimit ? "border-destructive focus-visible:ring-destructive" : undefined}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      // Form validates on blur; re-check on each keystroke once an
                      // error is showing so it clears the moment the user is back under the limit.
                      if (fieldState.error) void form.trigger(field.name);
                    }}
                  />
                </FormControl>
                <div className="flex items-start justify-between gap-2 text-xs">
                  {overLimit && !fieldState.error ? (
                    <p role="alert" className="flex items-center gap-1 text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      Too long by {length - TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH} — enter only the
                      confirmation number ({TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH} characters max).
                    </p>
                  ) : (
                    <span />
                  )}
                  <span className={overLimit ? "text-destructive font-medium" : "text-muted-foreground"}>
                    {length}/{TRAMS_CONFIRMATION_NUMBER_MAX_LENGTH}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        )}

        {/* Date Range */}
        {showTravelDatesField && (
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">Travel Dates *</legend>
          <div className="flex items-center gap-2">
            <FormField
              control={form.control}
              name="departureDate"
              render={({ field }) => (
                <FormItem className="flex-1 space-y-0">
                  <FormLabel className="text-xs text-muted-foreground">
                    Depart
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <span className="text-muted-foreground mt-5">–</span>
            <FormField
              control={form.control}
              name="returnDate"
              render={({ field }) => (
                <FormItem className="flex-1 space-y-0">
                  <FormLabel className="text-xs text-muted-foreground">
                    Return
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ""}
                      min={departureDate || undefined}
                      onClick={() => {
                        if (!field.value && departureDate) {
                          field.onChange(departureDate);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          (e.key === "ArrowDown" || e.key === " ") &&
                          !field.value &&
                          departureDate
                        ) {
                          field.onChange(departureDate);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </fieldset>
        )}

        {/* Financial Fields — hidden in the unclaimed-claim wizard which
            pre-populates these values from the TRAMS snapshot. The fields
            are ALWAYS mounted (so `react-hook-form` keeps tracking +
            submitting them even if `shouldUnregister` is ever flipped on);
            we use the native `hidden` attribute (not sr-only) so they stay
            out of the tab order and assistive tree while hidden. */}
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          hidden={hideFinancialFields || undefined}
          aria-hidden={hideFinancialFields || undefined}
        >
          <FormField
            control={form.control}
            name="totalFare"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Total Invoice Amount (USD) *</FormLabel>
                {/* AR-210 (TC-09.1, TC-11.1, TC-27.2): FormControl passes the label's id to
                    its direct child, which was the wrapper div, so the input had no name. */}
                <div className="relative">
                  <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      className="pl-7"
                      tabIndex={hideFinancialFields ? -1 : undefined}
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => {
                        const cleaned = stripMoneyFormatting(field.value ?? "");
                        if (cleaned && cleaned !== field.value) {
                          field.onChange(cleaned);
                        }
                      }}
                      onBlur={(e) => {
                        field.onChange(formatMoneyFieldValue(e.target.value));
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="commissionAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Commission Amount (USD) *</FormLabel>
                {/* AR-210 (TC-09.1, TC-11.1, TC-27.2): FormControl passes the label's id to
                    its direct child, which was the wrapper div, so the input had no name. */}
                <div className="relative">
                  <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      className="pl-7"
                      tabIndex={hideFinancialFields ? -1 : undefined}
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => {
                        const cleaned = stripMoneyFormatting(field.value ?? "");
                        if (cleaned && cleaned !== field.value) {
                          field.onChange(cleaned);
                        }
                      }}
                      onBlur={(e) => {
                        field.onChange(formatMoneyFieldValue(e.target.value));
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
    </>
  );

  const tramsInvoiceLabel = formatTramsInvoiceDisplayNumber(tramsInvoiceNumber);

  if (compactMode) {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-card p-3">
        {cardContent}
      </div>
    );
  }

  return (
    <Card className="relative">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Invoice #
            {tramsInvoiceLabel !== '—' ? tramsInvoiceLabel : index + 1}
          </CardTitle>
          {canRemove && onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{cardContent}</CardContent>
    </Card>
  );
}
